import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { WorkflowSearchTracker } from '@/lib/order-workflow-contract'
import type { ComboboxOption } from '@/components/ui/combobox'
import type { ProductLot } from '@/lib/api/product-lot-api'
import { Combobox } from '@/components/ui/combobox'
import { datesLine } from '@/components/product-lots/product-lot-option'
import { LotOptionRow, createdAtLine } from '@/components/lots/lot-option-row'
import { useProductLotsInfiniteQuery } from '@/hooks/use-product-lot-query'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface OrderLotPickerProps {
    workflow?: WorkflowSearchTracker
    productId: string
    warehouseId?: string
    value?: string
    /** Emit cả object lô để caller biết tồn khả dụng (validate "không đủ"). */
    onChange: (lot: ProductLot) => void
    /** Lô gợi ý/đã chọn để render nhãn dù không nằm trong trang search hiện tại. */
    seedLots?: ProductLot[]
    disabled?: boolean
}

function useDebounce(value: string, delay: number) {
    const [debounced, setDebounced] = useState(value)
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay)
        return () => clearTimeout(t)
    }, [value, delay])
    return debounced
}

// Option lô cho popup xuất kho: KHÔNG hiển thị tên sản phẩm (đã có ở title của
// section). Trigger (label) = mã lô + vị trí; dropdown row = mã lô (đậm) + vị trí
// + SL + NSX/HSD.
function lotOptionWithPosition(lot: ProductLot): ComboboxOption {
    const pos = lot.position_code ?? lot.position_name
    const unit = lot.product_unit ?? ''
    const qty = `${formatQty(lot.quantity_available)}${unit ? ` ${unit}` : ''}`
    const label = pos
        ? `${lot.code} · ${m.stock_out_lot_position({ code: pos })}`
        : lot.code
    return {
        value: lot.id,
        label,
        render: () => (
            <LotOptionRow
                title={lot.code}
                code=""
                chips={[lot.position_code]}
                qty={qty}
                thirdLine={datesLine(lot.manufactured_date, lot.expiry_date)}
                createdLine={createdAtLine(lot.created_at)}
                imageSlot
                imageUrl={lot.product_image_url}
            />
        ),
    }
}

export function OrderLotPicker({
    productId,
    warehouseId,
    value,
    onChange,
    seedLots,
    disabled,
    workflow,
}: OrderLotPickerProps) {
    const [search, setSearch] = useState('')
    const [open, setOpen] = useState(false)
    const searchId = useMemo(
        () => (workflow ? crypto.randomUUID() : undefined),
        [workflow, search, productId, warehouseId],
    )
    const lastResult = useRef('')
    const debouncedSearch = useDebounce(search, 300)

    const {
        data,
        isLoading,
        isFetching,
        isError,
        isPlaceholderData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useProductLotsInfiniteQuery(
        {
            product_id: productId,
            warehouse_id: warehouseId,
            search: debouncedSearch || undefined,
            only_available: '1',
            // FD-397: không bày lô ở kệ hàng lỗi đang tắt bán.
            sellable_only: '1',
            per_page: 20,
        },
        { enabled: Boolean(productId) },
    )

    const lotList = useMemo(
        () => (data?.pages ?? []).flatMap((p) => p.data ?? []),
        [data],
    )
    const settled = Boolean(
        productId &&
        data &&
        search === debouncedSearch &&
        !isLoading &&
        !isFetching &&
        !isFetchingNextPage &&
        !isError &&
        !isPlaceholderData,
    )
    useEffect(() => {
        if (!workflow || !open || !searchId || !settled) return
        const key = `${searchId}:${lotList.length}:${Boolean(hasNextPage)}`
        if (lastResult.current === key) return
        lastResult.current = key
        workflow.search({
            searchId,
            entityKind: 'lot',
            queryKind: search ? 'search' : 'browse',
            queryLength: debouncedSearch.length,
            resultCount: lotList.length,
            hasMore: Boolean(hasNextPage),
        })
    }, [
        workflow,
        open,
        searchId,
        settled,
        lotList.length,
        hasNextPage,
        search,
        debouncedSearch,
    ])
    const options = useMemo(() => lotList.map(lotOptionWithPosition), [lotList])

    // Cache option đã chọn + seed từ gợi ý để nhãn không rớt khỏi trigger khi
    // search dùng chung đổi (server chỉ trả 1 trang theo từ khoá hiện tại).
    const [picked, setPicked] = useState<Map<string, ComboboxOption>>(new Map())
    const seedMap = useMemo(() => {
        const map = new Map<string, ComboboxOption>()
        for (const l of seedLots ?? []) map.set(l.id, lotOptionWithPosition(l))
        return map
    }, [seedLots])

    const handleChange = useCallback(
        (id: string) => {
            const lot =
                lotList.find((l) => l.id === id) ??
                (seedLots ?? []).find((l) => l.id === id)
            if (!lot) return
            const opt = lotOptionWithPosition(lot)
            setPicked((prev) => {
                if (prev.has(id)) return prev
                const next = new Map(prev)
                next.set(id, opt)
                return next
            })
            const index = lotList.findIndex((candidate) => candidate.id === id)
            workflow?.step(
                'lot_selected',
                settled && index >= 0
                    ? { searchId, candidateRank: index + 1 }
                    : {},
            )
            onChange(lot)
        },
        [lotList, seedLots, onChange, workflow, searchId, settled],
    )

    const selectedOption = value
        ? (picked.get(value) ?? seedMap.get(value))
        : undefined

    const handleOpenChange = useCallback((nextOpen: boolean) => {
        setOpen(nextOpen)
        if (!nextOpen) setSearch('')
    }, [])

    return (
        <Combobox
            options={options}
            value={value ?? ''}
            onChange={handleChange}
            onSearchChange={setSearch}
            shouldFilter={false}
            placeholder={m.product_lot_picker_placeholder()}
            searchPlaceholder={m.product_lot_picker_search()}
            loading={isLoading}
            disabled={disabled}
            selectedOption={selectedOption}
            onOpenChange={handleOpenChange}
            onLoadMore={() => void fetchNextPage()}
            hasMore={Boolean(hasNextPage)}
            loadingMore={isFetchingNextPage}
        />
    )
}
